import type { LoggedMeal, MealType } from "./types";

export interface Suggestion {
  signature: string;
  count: number;
  lastSeen: string;
  representative: LoggedMeal;
}

function signature(meal: LoggedMeal): string {
  return meal.estimate.items
    .map((i) => i.name.trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join(" | ");
}

export function computeFrequent(
  meals: LoggedMeal[],
  opts: { mealType?: MealType; limit?: number } = {}
): Suggestion[] {
  const filtered = opts.mealType ? meals.filter((m) => m.meal_type === opts.mealType) : meals;
  const groups = new Map<string, LoggedMeal[]>();
  for (const m of filtered) {
    const sig = signature(m);
    if (!sig) continue;
    const arr = groups.get(sig) ?? [];
    arr.push(m);
    groups.set(sig, arr);
  }
  return Array.from(groups.entries())
    .map(([sig, ms]) => ({
      signature: sig,
      count: ms.length,
      lastSeen: ms[0].logged_at,
      representative: ms[0]
    }))
    .filter((s) => s.count >= 2) // only show as "frequent" when seen 2+ times
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return b.lastSeen.localeCompare(a.lastSeen);
    })
    .slice(0, opts.limit ?? 5);
}

export function computeRecent(meals: LoggedMeal[], limit = 5, excludeSignatures: Set<string> = new Set()): Suggestion[] {
  const seen = new Set<string>(excludeSignatures);
  const result: Suggestion[] = [];
  for (const m of meals) {
    const sig = signature(m);
    if (!sig || seen.has(sig)) continue;
    seen.add(sig);
    result.push({ signature: sig, count: 1, lastSeen: m.logged_at, representative: m });
    if (result.length >= limit) break;
  }
  return result;
}

/**
 * Search the user's logged meal history for items whose names match a query.
 * Used by AddFoodPicker to surface "you've eaten this before" at the top of
 * search results — fixes the painful UX where a previously-scanned barcode
 * item doesn't surface when you type its name later.
 *
 * Ranking: matches with more recent lastSeen + higher use count score higher.
 * Dedupes by item NAME (not full-meal signature) because a barcode-scanned
 * "Diet Coke" should match whether it was logged alone or alongside other
 * items.
 *
 * Returns Suggestion[] with the most-recent representative meal containing
 * that item — the existing quickLogMeal flow can re-log it directly.
 */
export function searchMealHistory(
  meals: LoggedMeal[],
  query: string,
  limit = 5
): Suggestion[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];

  // Group matching items by name (case-insensitive). For each name, track the
  // most recent meal containing it + total count across all meals.
  type Hit = { name: string; count: number; lastSeen: string; representative: LoggedMeal };
  const byName = new Map<string, Hit>();

  for (const m of meals) {
    for (const it of m.estimate.items) {
      const name = (it.name ?? "").trim();
      if (!name) continue;
      const lname = name.toLowerCase();
      if (!lname.includes(q)) continue;
      const existing = byName.get(lname);
      if (existing) {
        existing.count += 1;
        if (m.logged_at > existing.lastSeen) {
          existing.lastSeen = m.logged_at;
          existing.representative = m;
        }
      } else {
        byName.set(lname, { name, count: 1, lastSeen: m.logged_at, representative: m });
      }
    }
  }

  return Array.from(byName.values())
    .sort((a, b) => {
      // Recency dominates — last 7 days beats anything older.
      // After that, fall back to count.
      if (b.lastSeen !== a.lastSeen) return b.lastSeen.localeCompare(a.lastSeen);
      return b.count - a.count;
    })
    .slice(0, limit)
    .map((hit) => ({
      signature: hit.name.toLowerCase(),
      count: hit.count,
      lastSeen: hit.lastSeen,
      representative: hit.representative
    }));
}
